import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { getPathLanguage } from "@/lib/i18n";
import { translateText } from "@/lib/translations";
import axios from "axios";
import { Button } from "@/components/ui/button";

const MAX_SECONDS = 30;
const SUPPORTED_MIME_TYPES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/mp4",
];

export default function ClientReviewPage() {
  const { slug } = useParams();
  const location = useLocation();
  const language = getPathLanguage(location.pathname);
  const t = (text) => translateText(text, language);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [recording, setRecording] = useState(false);
  const [timeLeft, setTimeLeft] = useState(MAX_SECONDS);
  const [submitting, setSubmitting] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`/review-pages/${slug}?lang=${language}`).then(r => setSettings(r.data)).catch(() => setError("This review page is unavailable."));
  }, [language, slug]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const stopRecording = async () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };

  const startRecording = async (facingMode = "environment") => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("Camera recording is not supported by this browser.");
      return;
    }

    let stream;
    try {
      const cameraConstraints = facingMode ? { facingMode: { ideal: facingMode } } : true;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: cameraConstraints, audio: true });
      } catch (cameraError) {
        if (["NotAllowedError", "SecurityError", "PermissionDeniedError"].includes(cameraError?.name)) {
          throw cameraError;
        }
        console.warn("Preferred camera unavailable, falling back to default camera:", cameraError);
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      }
    } catch (permissionError) {
      setError("Camera or microphone permission was denied. Please allow access and try again.");
      return;
    }

    streamRef.current = stream;
    videoRef.current.srcObject = stream;
    chunksRef.current = [];
    const preferredMimeType = SUPPORTED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
    const recorder = new MediaRecorder(stream, preferredMimeType ? { mimeType: preferredMimeType } : undefined);
    recorderRef.current = recorder;
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || preferredMimeType || "video/webm" });
      const extension = blob.type.includes("mp4") ? "mp4" : "webm";
      const form = new FormData();
      form.append("file", blob, `review-${Date.now()}.${extension}`);
      setSubmitting(true);
      try { await axios.post(`/review-pages/${slug}/submit?lang=${language}`, form); alert(t("Thank you! Your review has been submitted.")); }
      catch { setError("Upload failed. Please try again."); }
      setSubmitting(false);
      stream.getTracks().forEach((track) => track.stop());
      if (streamRef.current === stream) streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
      setTimeLeft(MAX_SECONDS);
    };
    recorder.start();
    setRecording(true);
    setTimeLeft(MAX_SECONDS);
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) { stopRecording(); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  if (error) return <div className="p-6">{error}</div>;
  if (!settings) return <div className="p-6">Loading...</div>;

  return <div className="max-w-xl mx-auto p-4 space-y-4 text-center">
    {settings.logo_url ? <img src={settings.logo_url} alt="Logo" className="h-20 mx-auto object-contain" /> : null}
    <h1 className="text-2xl font-bold">{settings.company_name}</h1>
    <p className="text-slate-700">{settings.page_text}</p>
    <div className="flex flex-col sm:flex-row justify-center gap-3">
      {!recording ? <>
        <Button onClick={() => startRecording("environment")} disabled={submitting}>{t("Use rear camera")}</Button>
        <Button onClick={() => startRecording("user")} disabled={submitting} variant="outline">{t("Use front camera")}</Button>
      </> : <Button onClick={stopRecording} variant="destructive">Stop</Button>}
    </div>
    <p className="font-semibold">{recording ? `Time left: ${timeLeft}s` : t(`Max duration: ${MAX_SECONDS}s`)}</p>
    <p className="text-sm text-slate-600">{settings.consent_text}</p>
    <video ref={videoRef} autoPlay playsInline muted className="mx-auto w-full max-w-xs aspect-[9/16] rounded bg-black object-cover" />
  </div>;
}
